import {
	Stack,
	Paper,
	Box,
	Typography,
	Divider,
	Grid,
	Chip,
	Button,
	Alert,
	IconButton,
	Tooltip,
} from '@mui/material'
import {
	Person,
	Email,
	LocationOn,
	Category,
	Description,
	Edit,
	PictureAsPdf,
	Image,
	Download,
	Visibility,
} from '@mui/icons-material'
import React, { useMemo, useEffect, useState } from 'react'
import { ScaffoldRequestForm } from '../../../types/Scaffold/ScaffoldRequest'
import { getDecisionIcon, getDecisionColor, getDecisionSeverity } from './DecisionUtils'
import { ImageSlider } from '../../core/ImageSlider'
import { getBlobs } from '../../../services/CommonServices/imageAttachmentService'
import { ViewAttachmentsPopup } from '../../attachments/ViewAttachmentsPopup'
import { AttachmentInfo, BlobAddress } from '../../../types/Upload/AttachmentResponse'
import { ApiResponse } from '../../../services/CommonServices/httpService'

interface RequestInfoSectionProps {
	scaffoldRequest: ScaffoldRequestForm
	onUpdateDecision: () => void
}

export default function RequestInfoSection({
	scaffoldRequest,
	onUpdateDecision,
}: RequestInfoSectionProps) {
	const [imageBlobs, setImageBlobs] = useState<Blob[]>([])
	const [isLoadingImages, setIsLoadingImages] = useState(false)
	const [pdfViewerOpen, setPdfViewerOpen] = useState(false)

	// Separate attachments by type
	const { imageAttachments, pdfAttachments } = useMemo(() => {
		if (!scaffoldRequest.attachments || scaffoldRequest.attachments.length === 0) {
			return { imageAttachments: [], pdfAttachments: [] }
		}

		const images: typeof scaffoldRequest.attachments = []
		const pdfs: Array<{ id: number; fileName: string; downloadUrl: string }> = []

		scaffoldRequest.attachments.forEach((attachment) => {
			const extension = attachment.fileName.toLowerCase().split('.').pop()
			if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension || '')) {
				images.push(attachment)
			} else if (extension === 'pdf') {
				pdfs.push({
					id: attachment.id,
					fileName: attachment.fileName,
					downloadUrl: `${attachment.uploadUri}${attachment.sasToken}`,
				})
			}
		})

		return { imageAttachments: images, pdfAttachments: pdfs }
	}, [scaffoldRequest.attachments])

	// Create attachment operations object for ViewAttachmentsPopup
	const attachmentOperations = useMemo(
		() => ({
			listAttachments: async (): Promise<AttachmentInfo[]> => {
				// Convert PDF attachments to AttachmentInfo format
				return pdfAttachments.map((pdf) => ({
					id: pdf.id,
					name: pdf.fileName,
				}))
			},
			getAttachment: async (attachmentId: number): Promise<ApiResponse<BlobAddress> | null> => {
				const pdf = pdfAttachments.find((p) => p.id === attachmentId)
				if (pdf) {
					// Return a mock response that mimics the expected structure
					return {
						success: true,
						data: {
							uri: pdf.downloadUrl.split('?')[0],
							sasToken: `?${pdf.downloadUrl.split('?')[1]}`,
							id: pdf.id,
							name: pdf.fileName,
						},
					}
				}
				return null
			},
			uploadAttachment: async (): Promise<boolean> => {
				return false
			},
			deleteAttachment: async (): Promise<boolean> => {
				return false
			},
		}),
		[pdfAttachments, scaffoldRequest.requestorName],
	)

	// Load image blobs when image attachments change
	useEffect(() => {
		const loadImageBlobs = async () => {
			if (imageAttachments.length === 0) {
				setImageBlobs([])
				return
			}

			setIsLoadingImages(true)
			try {
				const blobs = await getBlobs(imageAttachments)
				setImageBlobs(blobs)
			} catch (error) {
				console.error('Error loading image blobs:', error)
				setImageBlobs([])
			} finally {
				setIsLoadingImages(false)
			}
		}

		loadImageBlobs()
	}, [imageAttachments])

	const handleDownloadPdf = (downloadUrl: string, fileName: string) => {
		const link = document.createElement('a')
		link.href = downloadUrl
		link.download = fileName
		document.body.appendChild(link)
		link.click()
		document.body.removeChild(link)
	}

	return (
		<>
			<Paper
				variant='outlined'
				sx={{
					p: 2,
					backgroundColor: '#f8f9fa',
					border: '1px solid #e0e0e0',
				}}
			>
				<Stack spacing={2}>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							flexWrap: 'wrap',
							gap: 2,
						}}
					>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
							<Typography variant='h6' sx={{ fontWeight: 600, color: 'primary.main' }}>
								Request Details
							</Typography>
							{scaffoldRequest.approvalDecision && (
								<>
									<Divider orientation='vertical' flexItem sx={{ height: 24 }} />
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
										<Chip
											icon={getDecisionIcon(scaffoldRequest.approvalDecision) || undefined}
											label={scaffoldRequest.approvalDecision}
											color={getDecisionColor(scaffoldRequest.approvalDecision)}
											variant='filled'
											size='small'
											sx={{ fontWeight: 600 }}
										/>
										{scaffoldRequest.lastDecisionBy && (
											<>
												<Typography variant='body1' color='text.secondary' sx={{ mx: 0.5 }}>
													•
												</Typography>
												<Typography variant='body1' color='text.secondary'>
													by {scaffoldRequest.lastDecisionBy}
												</Typography>
											</>
										)}
									</Box>
								</>
							)}
						</Box>
						<Button
							variant='outlined'
							size='small'
							startIcon={<Edit fontSize='small' />}
							onClick={onUpdateDecision}
							sx={{ minWidth: '140px', color: 'primary.main' }}
						>
							{scaffoldRequest.approvalDecision ? 'Update Decision' : 'Set Decision'}
						</Button>
					</Box>
					<Divider />

					<Grid container spacing={3}>
						{/* Requestor Information */}
						<Grid item xs={12} md={6}>
							<Stack spacing={2}>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
									<Typography
										variant='subtitle2'
										color='text.secondary'
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: 1,
											minWidth: '70px',
										}}
									>
										<Person fontSize='small' />
										Requestor:
									</Typography>
									<Typography variant='body1' sx={{ fontWeight: 500 }}>
										{scaffoldRequest.requestorName}
									</Typography>
								</Box>

								<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
									<Typography
										variant='subtitle2'
										color='text.secondary'
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: 1,
											minWidth: '120px',
										}}
									>
										<Email fontSize='small' />
										Requestor Email:
									</Typography>
									<Typography variant='body1'>{scaffoldRequest.requestorEmail}</Typography>
								</Box>
							</Stack>
						</Grid>

						{/* Site Contact Information */}
						<Grid item xs={12} md={6}>
							<Stack spacing={2}>
								<Box sx={{ display: 'flex', alignItems: 'center' }}>
									<Typography
										variant='subtitle2'
										color='text.secondary'
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: 1,
											minWidth: '120px',
										}}
									>
										<Person fontSize='small' />
										Site Contact:
									</Typography>
									<Typography variant='body1' sx={{ fontWeight: 500 }}>
										{scaffoldRequest.siteContact}
									</Typography>
								</Box>

								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
									<Typography
										variant='subtitle2'
										color='text.secondary'
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: 1,
											minWidth: '120px',
										}}
									>
										<Email fontSize='small' />
										Site Contact Email:
									</Typography>
									<Typography variant='body1'>{scaffoldRequest.siteContactEmail}</Typography>
								</Box>
							</Stack>
						</Grid>

						{/* Location Information */}
						<Grid item xs={12} md={6}>
							<Stack spacing={2}>
								<Box sx={{ display: 'flex', alignItems: 'center' }}>
									<Typography
										variant='subtitle2'
										color='text.secondary'
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: 1,
											minWidth: '70px',
										}}
									>
										<LocationOn fontSize='small' />
										Area:
									</Typography>
									<Typography variant='body1' sx={{ fontWeight: 500 }}>
										{scaffoldRequest.area}
									</Typography>
								</Box>

								<Box sx={{ maxWidth: '80%' }}>
									<Typography
										variant='subtitle2'
										color='text.secondary'
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: 1,
											mb: 1,
										}}
									>
										<LocationOn fontSize='small' />
										Exact Location:
									</Typography>
									<Typography
										variant='body1'
										sx={{
											whiteSpace: 'pre-wrap',
											wordBreak: 'break-word',
											textAlign: 'left',
										}}
									>
										{scaffoldRequest.exactLocation}
									</Typography>
								</Box>
							</Stack>
						</Grid>
						{/* Additional Information */}
						<Grid item xs={12} md={6}>
							<Stack spacing={2}>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
									<Typography
										variant='subtitle2'
										color='text.secondary'
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: 1,
											minWidth: '70px',
										}}
									>
										<Category fontSize='small' />
										Discipline:
									</Typography>
									<Chip
										label={scaffoldRequest.discipline}
										variant='outlined'
										color='primary'
										size='small'
									/>
								</Box>

								{/* PO and Work Order Information */}
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
									<Typography
										variant='subtitle2'
										color='text.secondary'
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: 1,
											minWidth: '70px',
										}}
									>
										<Category fontSize='small' />
										PO Number:
									</Typography>
									<Typography variant='body1' sx={{ fontWeight: 500 }}>
										{scaffoldRequest.po}
									</Typography>
								</Box>

								<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
									<Typography
										variant='subtitle2'
										color='text.secondary'
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: 1,
											minWidth: '120px',
										}}
									>
										<Category fontSize='small' />
										Work Order:
									</Typography>
									<Typography variant='body1' sx={{ fontWeight: 500 }}>
										{scaffoldRequest.workOrder}
									</Typography>
								</Box>

								<Box sx={{ maxWidth: '80%' }}>
									<Typography
										variant='subtitle2'
										color='text.secondary'
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: 1,
											mb: 1,
										}}
									>
										<Description fontSize='small' />
										Notes:
									</Typography>
									<Typography
										variant='body1'
										sx={{
											fontStyle: 'italic',
											whiteSpace: 'pre-wrap',
											wordBreak: 'break-word',
											textAlign: 'left',
										}}
									>
										{scaffoldRequest.notes || 'No notes provided'}
									</Typography>
								</Box>
							</Stack>
						</Grid>
					</Grid>

					{/* Decision Comment Section */}
					{scaffoldRequest.decisionComment && (
						<>
							<Divider sx={{ my: 2 }} />
							<Box>
								<Typography
									variant='subtitle2'
									color='text.secondary'
									sx={{ mb: 1, fontWeight: 600, textAlign: 'left' }}
								>
									Comment:
								</Typography>
								<Alert
									severity={getDecisionSeverity(scaffoldRequest.approvalDecision)}
									sx={{
										backgroundColor: 'action.hover',
										'& .MuiAlert-message': {
											width: '100%',
										},
									}}
								>
									<Typography
										variant='body2'
										sx={{
											whiteSpace: 'pre-wrap',
											wordBreak: 'break-word',
											textAlign: 'left',
										}}
									>
										{scaffoldRequest.decisionComment}
									</Typography>
								</Alert>
							</Box>
						</>
					)}
				</Stack>
			</Paper>

			{/* Attachments Section */}
			{(imageAttachments.length > 0 || pdfAttachments.length > 0) && (
				<Paper
					variant='outlined'
					sx={{
						p: 2,
						backgroundColor: '#f8f9fa',
						border: '1px solid #e0e0e0',
					}}
				>
					<Stack spacing={2}>
						<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
							<Typography variant='h6' sx={{ fontWeight: 600, color: 'primary.main' }}>
								Attachments
							</Typography>
						</Box>
						<Divider />

						{/* Image Attachments */}
						{imageAttachments.length > 0 && (
							<Box>
								<Typography
									variant='subtitle1'
									sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
								>
									<Image fontSize='small' />
									Images ({imageAttachments.length})
								</Typography>
								{isLoadingImages ? (
									<Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
										<Typography variant='body2' color='text.secondary'>
											Loading images...
										</Typography>
									</Box>
								) : imageBlobs.length > 0 ? (
									<Box sx={{ display: 'flex', justifyContent: 'center' }}>
										<ImageSlider blobs={imageBlobs} readOnly={true} />
									</Box>
								) : (
									<Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
										<Typography variant='body2' color='text.secondary'>
											Failed to load images
										</Typography>
									</Box>
								)}
							</Box>
						)}

						{/* PDF Attachments */}
						{pdfAttachments.length > 0 && (
							<Box>
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between',
										mb: 2,
									}}
								>
									<Typography
										variant='subtitle1'
										sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
									>
										<PictureAsPdf fontSize='small' />
										PDF Documents ({pdfAttachments.length})
									</Typography>
									<Button
										variant='outlined'
										size='small'
										startIcon={<Visibility />}
										onClick={() => setPdfViewerOpen(true)}
										sx={{ ml: 2, color: 'primary.main' }}
									>
										Open PDF Viewer
									</Button>
								</Box>
								<Stack spacing={1}>
									{pdfAttachments.map((pdf) => (
										<Box
											key={pdf.id}
											sx={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'space-between',
												p: 1,
												border: '1px solid',
												borderColor: 'divider',
												borderRadius: 1,
												backgroundColor: 'background.default',
											}}
										>
											<Typography variant='body2' sx={{ flex: 1 }}>
												{pdf.fileName}
											</Typography>
											<Tooltip title='Download PDF'>
												<IconButton
													size='small'
													onClick={() => handleDownloadPdf(pdf.downloadUrl, pdf.fileName)}
													sx={{ color: 'primary.main' }}
												>
													<Download fontSize='small' />
												</IconButton>
											</Tooltip>
										</Box>
									))}
								</Stack>
							</Box>
						)}
					</Stack>
				</Paper>
			)}

			{/* PDF Viewer Popup */}
			{pdfAttachments.length > 0 && pdfViewerOpen && (
				<ViewAttachmentsPopup
					open={pdfViewerOpen}
					handleClose={() => setPdfViewerOpen(false)}
					itemId={scaffoldRequest.id || 0}
					attachmentOperations={attachmentOperations}
				/>
			)}
		</>
	)
}
